/**
 * Схема валидации профиля дела (zod).
 *
 * Схема отвечает за СТРУКТУРУ: типы, обязательность на уровне формата,
 * контрольные разряды. Правовые и содержательные проверки — в `rules/readiness.ts`,
 * потому что они делятся на блокирующие и предупреждающие, а схема так не умеет.
 *
 * Один и тот же файл используется формами клиента (react-hook-form + zodResolver)
 * и сервером при приёме тела запроса на генерацию.
 */

import { z } from 'zod';
import { isValidInn } from '../validators/inn.js';
import { isValidSnils } from '../validators/snils.js';

/**
 * Дата: «ГГГГ-ММ-ДД» либо «не указана».
 *
 * Пустая строка приводится к «не указана» ПЕРЕД проверкой. Поля карточки
 * пустое значение и так обращают в null, но в профиле, заведённом раньше,
 * пустая строка встречается — и одна такая строка делала невалидным ВЕСЬ
 * профиль. Последствия шире, чем кажется: этой же проверкой профиль
 * проходит перед сборкой пакета, то есть дело нельзя было ни сохранить
 * в резервную копию, ни собрать по нему документы. По одному делу таких
 * отказов накопилось полторы сотни, и все молча.
 *
 * Терпимость на входе, строгость дальше: в профиле после разбора остаётся
 * либо дата нужного вида, либо null — то и другое документы обрабатывают.
 */
const isoDate = z.preprocess(
  (value) => (value === '' ? null : value),
  z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата в формате ГГГГ-ММ-ДД')
    .nullable(),
);

/** Суммы хранятся в копейках целыми числами. */
const kopecks = z.number().int('Сумма хранится в копейках целым числом').min(0);

const optionalString = z.string().default('');

export const addressSchema = z.object({
  full: optionalString,
  postalCode: z.string().optional(),
  region: z.string().optional(),
  area: z.string().optional(),
  city: z.string().optional(),
  settlement: z.string().optional(),
  street: z.string().optional(),
  house: z.string().optional(),
  block: z.string().optional(),
  flat: z.string().optional(),
  fiasId: z.string().optional(),
  kladrId: z.string().optional(),
  source: z.enum(['manual', 'dadata']).optional(),
  fullManual: z.boolean().optional(),
});

export const idDocumentSchema = z.object({
  kind: z.enum(['passport_rf', 'passport_foreign', 'residence_permit', 'temporary_id', 'other']),
  kindOther: z.string().optional(),
  series: optionalString,
  number: optionalString,
  issuedBy: optionalString,
  issuedAt: isoDate,
  departmentCode: z.string().optional(),
});

export const debtorSchema = z.object({
  last: optionalString,
  first: optionalString,
  middle: z.string().optional(),
  previousNames: z.array(z.string()).default([]),
  gender: z.enum(['male', 'female']).nullable(),
  birthDate: isoDate,
  birthPlace: optionalString,
  // Пустое значение допускается на этапе ввода: обязательность даёт readiness,
  // а схема ловит только заполненный, но неверный ИНН.
  inn: z.string().refine((v) => v === '' || isValidInn(v, 'person'), 'Неверный ИНН'),
  snils: z.string().refine((v) => v === '' || isValidSnils(v), 'Неверный СНИЛС'),
  idDocument: idDocumentSchema,
  registrationAddress: addressSchema,
  hasRegistration: z.boolean().default(true),
  stayAddress: addressSchema.optional(),
  phone: z.string().optional(),
  email: z.string().email('Неверный адрес электронной почты').or(z.literal('')).optional(),
});

const caseRecord = z.object({
  nominative: optionalString,
  genitive: optionalString,
  dative: optionalString,
  accusative: optionalString,
  instrumental: optionalString,
  prepositional: optionalString,
});

export const declensionSchema = z.object({
  fio: caseRecord,
  manuallyEdited: z.record(z.string(), z.boolean()).default({}),
});

export const courtSchema = z.object({
  id: z.string().nullable(),
  name: optionalString,
  address: optionalString,
  regionCode: z.string().optional(),
});

export const representativeSchema = z.object({
  enabled: z.boolean().default(true),
  name: optionalString,
  inn: z.string().refine((v) => v === '' || isValidInn(v), 'Неверный ИНН представителя'),
  address: optionalString,
  phone: optionalString,
  email: optionalString,
  powerOfAttorney: optionalString,
});

export const creditorSchema = z.object({
  id: z.string(),
  obligationType: optionalString,
  name: optionalString,
  dictionaryId: z.string().nullable().optional(),
  inn: z.string().optional(),
  address: optionalString,
  basis: optionalString,
  basisDetails: optionalString,
  // Две суммы: графа формы разделена на «всего» и «в том числе задолженность»
  amountDebt: kopecks,
  amountTotal: kopecks.optional(),
  confirmingDocument: optionalString,
  isBusiness: z.boolean().default(false),
  overdueOverThreeMonths: z.boolean().default(false),
});

/** Налоги, сборы и взносы — отдельная таблица формы Приложения № 1. */
export const obligatoryPaymentSchema = z.object({
  id: z.string(),
  name: optionalString,
  arrears: kopecks,
  isBusiness: z.boolean().default(false),
  confirmingDocument: optionalString,
});

const propertyBase = {
  id: z.string(),
  ownership: z.enum(['individual', 'shared', 'joint']).default('individual'),
  share: z.string().optional(),
  acquisitionBasis: optionalString,
  cost: kopecks,
  pledge: z.string().optional(),
};

export const realtySchema = z.object({
  ...propertyBase,
  kind: z.enum(['land', 'house', 'flat', 'dacha', 'garage', 'other']),
  kindOther: z.string().optional(),
  cadastralNumber: optionalString,
  address: optionalString,
  area: z.number().nullable(),
});

export const vehicleSchema = z.object({
  ...propertyBase,
  kind: z.enum(['car', 'truck', 'moto', 'agricultural', 'water', 'air', 'other']),
  kindOther: z.string().optional(),
  model: optionalString,
  year: z.number().int().nullable(),
  identificationNumber: optionalString,
  storagePlace: optionalString,
});

export const bankAccountSchema = z.object({
  id: z.string(),
  bankName: optionalString,
  bankAddress: optionalString,
  accountKind: optionalString,
  currency: z.string().default('RUB'),
  openedAt: isoDate,
  balance: kopecks,
  accountNumber: z.string().optional(),
  cost: kopecks.default(0),
  share: z.string().optional(),
  pledge: z.string().optional(),
});

export const shareHoldingSchema = z.object({
  id: z.string(),
  organizationName: optionalString,
  ogrn: optionalString,
  inn: optionalString,
  address: optionalString,
  participationShare: optionalString,
  acquisitionBasis: optionalString,
  cost: kopecks,
  share: z.string().optional(),
  pledge: z.string().optional(),
});

export const securitySchema = z.object({
  id: z.string(),
  kind: optionalString,
  issuer: optionalString,
  nominal: kopecks,
  quantity: z.number().int().nullable(),
  acquisitionBasis: optionalString,
  cost: kopecks,
  share: z.string().optional(),
  pledge: z.string().optional(),
});

export const valuableSchema = z.object({
  id: z.string(),
  kind: z.enum(['cash', 'jewelry', 'art', 'professional', 'other']),
  kindOther: z.string().optional(),
  description: optionalString,
  storagePlace: optionalString,
  ownership: z.enum(['individual', 'shared', 'joint']).default('individual'),
  cost: kopecks,
  share: z.string().optional(),
  pledge: z.string().optional(),
});

export const propertySchema = z.object({
  hasProperty: z.boolean().default(false),
  realty: z.array(realtySchema).default([]),
  vehicles: z.array(vehicleSchema).default([]),
  accounts: z.array(bankAccountSchema).default([]),
  shares: z.array(shareHoldingSchema).default([]),
  securities: z.array(securitySchema).default([]),
  valuables: z.array(valuableSchema).default([]),
});

export const dealSchema = z.object({
  id: z.string(),
  date: isoDate,
  subject: optionalString,
  kind: optionalString,
  parties: optionalString,
  price: kopecks,
  documentDetails: optionalString,
});

export const familySchema = z.object({
  statuses: z.array(z.enum(['single', 'married', 'divorced'])).default([]),
  spouses: z
    .array(
      z.object({
        id: z.string(),
        fio: optionalString,
        relation: z.enum(['current', 'former']),
        marriedAt: isoDate,
        divorcedAt: isoDate,
      }),
    )
    .default([]),
  children: z
    .array(
      z.object({
        id: z.string(),
        fio: optionalString,
        birthDate: isoDate,
        dependent: z.boolean().default(true),
      }),
    )
    .default([]),
});

export const incomeSchema = z.object({
  employed: z.boolean().default(false),
  employerName: optionalString,
  employerInn: z.string().refine((v) => v === '' || isValidInn(v), 'Неверный ИНН работодателя'),
  monthlySalary: kopecks,
  salaryConfirmation: optionalString,
  hasSocialPayments: z.boolean().default(false),
  socialPaymentsDescription: optionalString,
  isEntrepreneur: z.boolean().default(false),
  entrepreneurOgrnip: z.string().optional(),
  hasOrganizations: z.boolean().default(false),
  organizations: z
    .array(
      z.object({
        id: z.string(),
        name: optionalString,
        inn: optionalString,
        ogrn: optionalString,
        address: optionalString,
        share: optionalString,
      }),
    )
    .default([]),
});

const dictionaryRef = z.object({ id: z.string().nullable(), name: optionalString });

export const legalSchema = z.object({
  sro: dictionaryRef.extend({
    fullName: z.string().optional(),
    fullNameGenitive: z.string().optional(),
    ogrn: z.string().optional(),
    inn: z.string().optional(),
    legalAddress: z.string().optional(),
    actualAddress: z.string().optional(),
    postalAddress: z.string().optional(),
  }),
  creditBureau: dictionaryRef,
  creditReportKind: optionalString,
  creditPurpose: optionalString,
  creditorCertificatesClause: z.enum(['general', 'partial']).default('general'),
  creditorCertificatesCreditors: z.string().optional(),
  extraAppendixItems: z
    .array(z.object({ id: z.string(), text: z.string(), position: z.number().int().nullable() }))
    .default([]),
  appendixChecklist: z.record(z.string(), z.boolean()).default({}),
  applicationDate: isoDate,
});

export const caseProfileSchema = z.object({
  schemaVersion: z.literal(1),
  revision: z.number().int().min(0),
  caseId: z.string(),
  stage: z.enum(['collecting', 'ready', 'draft_generated', 'review', 'returned', 'approved', 'filed']),
  debtor: debtorSchema,
  declension: declensionSchema,
  court: courtSchema,
  representative: representativeSchema,
  creditors: z.array(creditorSchema).default([]),
  obligatoryPayments: z.array(obligatoryPaymentSchema).default([]),
  property: propertySchema,
  deals: z.object({ hasDeals: z.boolean().default(false), items: z.array(dealSchema).default([]) }),
  family: familySchema,
  income: incomeSchema,
  legal: legalSchema,
  meta: z.object({
    updatedAt: z.string().nullable(),
    updatedByUserId: z.string().nullable(),
    approvedTemplateVersions: z.record(z.string(), z.string()).optional(),
  }),
});

export type CaseProfileInput = z.input<typeof caseProfileSchema>;

/** Разбор профиля из поля `UF_PROFILE_JSON`. Ошибки формулируются по-русски. */
export function parseProfile(value: unknown): { ok: true; profile: z.infer<typeof caseProfileSchema> } | { ok: false; errors: string[] } {
  const result = caseProfileSchema.safeParse(value);
  if (result.success) return { ok: true, profile: result.data };
  return {
    ok: false,
    errors: result.error.issues.map((i) => `${i.path.join('.') || 'профиль'}: ${i.message}`),
  };
}
